import { chosenSites as chosenSitesService } from "app/common/services/chosenSites";
import { AutocompleteWebsitesPageMainItem } from "pro-features/components/AutocompleteWebsites/AutocompleteWebsitesPageMainItem";
import type { IWebsiteTooltipProps } from "app/components/React/Tooltip/WebsiteTooltip/WebsiteTooltip";
import { WebsiteTooltip } from "app/components/React/Tooltip/WebsiteTooltip/WebsiteTooltip";
import type { ISimilarSite } from "./WebsiteQueryBar";
import { BREAK_POINT, DragHandleStyled, LoadingComponent, hoverMessagesObject } from "./WebsiteQueryBar";
import { translate } from "app/filters/i18nFilter";
import {
    FaviconButtonStyle,
    QueryBarWebsiteItemStyled,
    QueryBarItemWrapper,
    QueryBarVersusWrapper,
} from "../StyledComponent";
import AddWebsiteToListButtonContainer from "app/pages/sales-intelligence/sub-modules/account-review/AddToListButton/AddWebsiteToListButtonContainer";
import { FavoritesService } from "app/services/favorites/favoritesService";
import { FaviconButtonContainer } from "app/components/React/FavIcon/FaviconButtonContainer";
import { AutocompleteWebsitePagesCompareItem } from "pro-features/components/AutocompleteWebsites/AutocompleteWebsitePageCompareItem";
import type { IWebsitePageResult, WebsitePageSearchType } from "pro-features/components/AutocompleteWebsites/types";
import type { autocompleteStates } from "../WebsiteQueryBarTypes";
import type { ISerializable } from "app/services/solutions2Services/HomepageDataFetchers/utils";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { QueryBarItem } from "../QueryBarItem";
import type { IChosenItem } from "./WebsitePageQueryBarUtils";
import { getQueryBarSecondaryTextMap } from "./WebsitePageQueryBarUtils";
import { renderDefaultTextComponent, IconButton, PlainTooltip } from "@similarweb/ui-components";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { FC } from "react";

const HOVER_TOOLTIP_MAX_WIDTH = 1000;

const ConditionalWebsiteTooltip = ({
    searchType,
    children,
    domain,
    ...rest
}: { searchType: WebsitePageSearchType } & IWebsiteTooltipProps) => {
    return searchType === "folder" || searchType === "exact" ? (
        <PlainTooltip
            tooltipContent={domain}
            maxWidth={HOVER_TOOLTIP_MAX_WIDTH}
            placement="top-center"
        >
            <div>{children}</div>
        </PlainTooltip>
    ) : (
        <WebsiteTooltip
            {...rest}
            domain={domain}
        >
            {children}
        </WebsiteTooltip>
    );
};

interface IMainItemSearchComponentArgs {
    item: IChosenItem;
    recents: any;
    chosenItems: IChosenItem[];
    similarSites: ISimilarSite[];
    onClick: (item: IWebsitePageResult, type: string) => void;
    onClose: () => void;
    onPressEnter: (event: any, item: IWebsitePageResult, isMainItem: boolean) => void;
    hideCompareButton?: boolean;
}

const getMainItemSearchComponent = ({
    item,
    recents,
    chosenItems,
    similarSites,
    onClick,
    onClose,
    onPressEnter,
    hideCompareButton = false,
}: IMainItemSearchComponentArgs) => {
    const excludesWithoutCurrentItem = chosenItems.filter((chosenItem) => chosenItem.url !== item.url);
    return !chosenSitesService.isCompare() ? (
        <AutocompleteWebsitesPageMainItem
            className="AutocompleteWebsitePagesQueryBar"
            onClick={onClick}
            onClose={onClose}
            recentSearches={recents}
            overrideEnterFunc={(event, mainItem) => onPressEnter(event, mainItem, true)}
            defaultValue={item.page ? item.url : null}
            defaultSearchType={item.searchType}
            autocompleteProps={{
                placeholder: item.url,
                width: 500,
                top: 0,
            }}
            excludes={excludesWithoutCurrentItem}
            showCompareSites={!hideCompareButton}
        />
    ) : (
        <AutocompleteWebsitePagesCompareItem
            className="AutocompleteWebsitePagesQueryBar"
            secondColumnName="secondaryText"
            onClick={onClick}
            onCloseButtonClick={onClose}
            similarSites={similarSites}
            recentSearches={recents}
            defaultValue={item.page ? item.url : null}
            defaultSearchType={item.searchType}
            excludes={excludesWithoutCurrentItem}
            overrideEnterFunc={(event, compareItem) => onPressEnter(event, compareItem, false)}
            autocompleteProps={{
                placeholder: item.url,
                width: 500,
                top: 0,
            }}
        />
    );
};

interface ICompareListProps {
    shouldShowBadge: boolean;
    recents: autocompleteStates[] | ISerializable[];
    chosenItems: IChosenItem[];
    activeItem: string;
    isLoading: boolean;
    compareSitesToBeAdded: IChosenItem[];
    similarSites: ISimilarSite[];
    onClick: (item) => void;
    removeItem: (item) => () => void;
    onCompareItemAutocompleteItemClick: (item: IWebsitePageResult, type: string) => void;
    onCompareItemAutocompleteCloseButtonClick: (item) => void;
    onPressEnter: (event: any, item: any, isMainItem: boolean) => void;
    removeItemFromSitesToBeAddedList: (item) => void;
}

export const getCompareList = ({
    shouldShowBadge,
    chosenItems,
    activeItem,
    isLoading,
    compareSitesToBeAdded,
    similarSites,
    recents,
    onClick,
    removeItem,
    onCompareItemAutocompleteItemClick,
    onCompareItemAutocompleteCloseButtonClick,
    onPressEnter,
    removeItemFromSitesToBeAddedList,
}: ICompareListProps) => {
    const isSmallScreen = window.innerWidth <= BREAK_POINT;
    const excludes = [...chosenItems, ...compareSitesToBeAdded];

    const currentCompareList = chosenItems.slice(1).map((item: IChosenItem, index) => {
        const excludesWithoutCurrentItem = excludes.filter((chosenItem) => chosenItem.url !== item.url);
        return (
            <div key={item.url}>
                <QueryBarItem
                    id={item.url}
                    isLoading={isLoading}
                    activeItem={activeItem}
                    renderComponent={
                        <ConditionalWebsiteTooltip
                            domain={item.url}
                            placement="bottom-left"
                            debounce={1500}
                            appendTo={".QueryBar"}
                            searchType={item.searchType}
                        >
                            <FlexRow>
                                <DragHandleStyled />
                                <QueryBarWebsiteItemStyled
                                    text={item.url}
                                    image={item.icon ?? item.image}
                                    onItemClick={() => onClick(item.url)}
                                    onButtonClick={removeItem(item)}
                                    badgeColor={shouldShowBadge ? item.color : undefined}
                                    isCompare={true}
                                    secondaryText={getQueryBarSecondaryTextMap()[item.searchType]}
                                    textRenderFunction={(...args) => {
                                        const isPageLevel = item.searchType === "exact" || item.searchType === "folder";
                                        return renderDefaultTextComponent(
                                            args[0],
                                            isPageLevel ? args[1] : args[1]?.split("/")[0],
                                            args[2],
                                            isPageLevel,
                                        );
                                    }}
                                />
                            </FlexRow>
                        </ConditionalWebsiteTooltip>
                    }
                    searchComponent={
                        <AutocompleteWebsitePagesCompareItem
                            className="AutocompleteWebsitePagesQueryBar"
                            secondColumnName="secondaryText"
                            onClick={onCompareItemAutocompleteItemClick}
                            similarSites={similarSites}
                            defaultValue={item.page ? item.url : null}
                            defaultSearchType={item.searchType}
                            recentSearches={recents}
                            excludes={excludesWithoutCurrentItem}
                            onCloseButtonClick={onCompareItemAutocompleteCloseButtonClick}
                            overrideEnterFunc={(event, item) => onPressEnter(event, item, false)}
                            autocompleteProps={{
                                placeholder: `${activeItem ? activeItem : ""}`,
                                ...(isSmallScreen && index === 3 ? { width: 208 } : { width: 500 }),
                            }}
                        />
                    }
                    loadingComponent={
                        <LoadingComponent
                            loaderStyles={{
                                ...(isSmallScreen && index === 3 ? { width: 208 } : {}),
                            }}
                            placeholderText={item.url}
                            headerText={translate("autocomplete.websitePagesCompare.seperator.text.similarSites")}
                        />
                    }
                />
            </div>
        );
    });
    const newCompareList = compareSitesToBeAdded.map((item: IChosenItem) => {
        return (
            <QueryBarItem
                key={item.url}
                className="compare-site-to-be-added"
                id={item.url}
                renderComponent={
                    <QueryBarWebsiteItemStyled
                        text={item.url}
                        image={item.icon}
                        onItemClick={() => null}
                        onButtonClick={() => removeItemFromSitesToBeAddedList(item.url)}
                        isCompare={true}
                        secondaryText={getQueryBarSecondaryTextMap()[item.searchType]}
                        textRenderFunction={(...args) => {
                            const isPageLevel = item.searchType === "exact" || item.searchType === "folder";
                            return renderDefaultTextComponent(
                                args[0],
                                isPageLevel ? args[1] : args[1].split("/")[0],
                                args[2],
                                isPageLevel,
                            );
                        }}
                    />
                }
            />
        );
    });
    return [...currentCompareList, ...newCompareList];
};

interface IMainItemProps {
    shouldShowBadge: boolean;
    recents: autocompleteStates[] | ISerializable[];
    chosenItems: IChosenItem[];
    activeItem: string;
    isLoading: boolean;
    similarSites: ISimilarSite[];
    numOfVisibleCompareItems: number;
    removeItem: (item) => () => void;
    onMainItemTextClick: (url: string) => () => void;
    onMainItemAutocompleteItemClick: (item: any, type: string) => void;
    onMainItemAutocompleteClose: () => void;
    onPressEnter: (event: any, item: IWebsitePageResult, isMainItem: boolean) => void;
}

export const getMainItem = ({
    shouldShowBadge,
    chosenItems,
    activeItem,
    isLoading,
    similarSites,
    recents,
    numOfVisibleCompareItems,
    removeItem,
    onMainItemTextClick,
    onMainItemAutocompleteItemClick,
    onMainItemAutocompleteClose,
    onPressEnter,
}: IMainItemProps) => {
    const mainItem = chosenItems[0];
    return {
        id: mainItem.url,
        component: (
            <QueryBarItemWrapper
                key={mainItem.url}
                className="u-flex-row u-flex-center QueryBar-mainItem"
            >
                <QueryBarItem
                    id={mainItem.url}
                    isLoading={isLoading}
                    activeItem={activeItem}
                    renderComponent={
                        <ConditionalWebsiteTooltip
                            domain={mainItem.url}
                            placement="bottom-left"
                            debounce={1500}
                            appendTo={".QueryBar"}
                            searchType={mainItem.searchType}
                        >
                            <FlexRow>
                                <DragHandleStyled />
                                <QueryBarWebsiteItemStyled
                                    text={mainItem.url}
                                    image={mainItem.icon ? mainItem.icon : mainItem.image}
                                    onItemClick={onMainItemTextClick(mainItem.url)}
                                    onButtonClick={removeItem(mainItem)}
                                    badgeColor={shouldShowBadge ? mainItem.color : undefined}
                                    isCompare={chosenItems.length > 1}
                                    secondaryText={getQueryBarSecondaryTextMap()[mainItem.searchType]}
                                    textRenderFunction={(...args) => {
                                        const isPageLevel =
                                            mainItem.searchType === "exact" || mainItem.searchType === "folder";
                                        return renderDefaultTextComponent(
                                            args[0],
                                            isPageLevel ? args[1] : args[1].split("/")[0],
                                            args[2],
                                            isPageLevel,
                                        );
                                    }}
                                />
                                {numOfVisibleCompareItems > 0 && !proNavigator.current().hideCompareButton ? (
                                    <QueryBarVersusWrapper
                                        key={0}
                                        className="QueryBar-vs"
                                    >
                                        vs.
                                    </QueryBarVersusWrapper>
                                ) : null}
                            </FlexRow>
                        </ConditionalWebsiteTooltip>
                    }
                    searchComponent={getMainItemSearchComponent({
                        item: mainItem,
                        recents,
                        chosenItems: chosenItems,
                        similarSites,
                        onClick: onMainItemAutocompleteItemClick,
                        onClose: onMainItemAutocompleteClose,
                        onPressEnter,
                        hideCompareButton: proNavigator.current().hideCompareButton,
                    })}
                    loadingComponent={
                        <LoadingComponent
                            loaderStyles={{ width: 428 }}
                            placeholderText={mainItem.url}
                            headerText={translate(
                                chosenItems.length > 1
                                    ? "autocomplete.websitePagesCompare.seperator.text.similarSites"
                                    : "autocomplete.websitePagesMain.seperator.text.recents",
                            )}
                        />
                    }
                />
            </QueryBarItemWrapper>
        ),
    };
};

interface IFavoriteButton {
    mainSite: string;
    isAccountReview?: boolean;
}
export const FavoriteButton: FC<IFavoriteButton> = ({ mainSite, isAccountReview }) => (
    <FaviconButtonStyle>
        {isAccountReview ? (
            <AddWebsiteToListButtonContainer domain={mainSite} />
        ) : (
            <FaviconButtonContainer
                hoverMessages={hoverMessagesObject}
                isPageFavorite={FavoritesService.isCurrentPageFavorite()}
                trackEventName={
                    (chosenSitesService.get().length > 1 ? "Comparison" : "Site") +
                    "/Website Analysis/" +
                    chosenSitesService.get().join()
                }
            />
        )}
    </FaviconButtonStyle>
);

export const getButtonToDisplay = (onClick: () => void, hasComparedSites: boolean) => {
    const compareButtonText = proNavigator.current().compareButtonText ?? "analysis.header.btn";
    return (
        <div>
            <IconButton
                className={"QueryBar-compare-button"}
                onClick={onClick}
                iconName="add"
            >
                {hasComparedSites ? translate("analysis.header.btn.add") : translate(compareButtonText)}
            </IconButton>
        </div>
    );
};
